<script setup lang="ts">
const { t } = useI18n()
const localePath = useLocalePath()
const { fetchPages } = useStaticPage()
const supportEmail = useRuntimeConfig().public.businessEmail as string

useSeoMeta({
  title: () => `${t('helpCenter.title')} - MiDiGi`,
  description: () => t('helpCenter.description'),
})

useSeoCanonical('/help')

const pages = ref<any[]>([])
const loading = ref(true)

onMounted(async () => {
  try {
    pages.value = await fetchPages()
  }
  catch {
    pages.value = []
  }
  finally {
    loading.value = false
  }
})

function formatDate(date: string) {
  if (!date) return ''
  return new Date(date).toLocaleDateString()
}
</script>

<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-950">
    <!-- Header -->
    <div class="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
      <div class="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8 text-center">
        <UIcon name="i-lucide-life-buoy" class="w-12 h-12 text-brand-600 dark:text-brand-400 mx-auto mb-4" />
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl">
          {{ t('helpCenter.title') }}
        </h1>
        <p class="mt-3 text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
          {{ t('helpCenter.subtitle') }}
        </p>
      </div>
    </div>

    <div class="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
      <!-- Loading -->
      <div v-if="loading" class="space-y-4">
        <div v-for="i in 4" :key="i" class="bg-white dark:bg-gray-900 rounded-xl p-6 animate-pulse">
          <div class="h-5 bg-gray-200 dark:bg-gray-800 rounded w-1/3 mb-2" />
          <div class="h-4 bg-gray-200 dark:bg-gray-800 rounded w-1/5" />
        </div>
      </div>

      <!-- Empty -->
      <div v-else-if="pages.length === 0" class="text-center py-20">
        <UIcon name="i-lucide-file-x" class="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
        <p class="text-gray-500 dark:text-gray-400 text-lg">{{ t('helpCenter.empty') }}</p>
      </div>

      <!-- Pages List -->
      <div v-else class="space-y-3">
        <NuxtLink
          v-for="page in pages"
          :key="page.id"
          :to="localePath(`/p/${page.slug}`)"
          class="block bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 hover:border-brand-500 dark:hover:border-brand-500 hover:shadow-md transition-all group"
        >
          <div class="flex items-center justify-between">
            <div class="flex-1 min-w-0">
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                {{ page.title || page.slug }}
              </h2>
              <p v-if="page.metaDescription" class="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                {{ page.metaDescription }}
              </p>
              <p v-if="page.updatedAt" class="mt-1 text-xs text-gray-400 dark:text-gray-500">
                {{ t('staticPage.lastUpdated') }}: {{ formatDate(page.updatedAt) }}
              </p>
            </div>
            <UIcon name="i-lucide-chevron-right" class="w-5 h-5 text-gray-400 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors flex-shrink-0 ml-4" />
          </div>
        </NuxtLink>
      </div>

      <!-- Contact -->
      <div class="mt-12 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-8 text-center">
        <UIcon name="i-lucide-mail" class="w-8 h-8 text-brand-600 dark:text-brand-400 mx-auto mb-3" />
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          {{ t('helpCenter.contactTitle') }}
        </h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {{ t('helpCenter.contactDescription') }}
        </p>
        <a
          :href="`mailto:${supportEmail}`"
          class="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-medium transition-colors"
        >
          <UIcon name="i-lucide-send" class="w-4 h-4" />
          {{ supportEmail }}
        </a>
      </div>
    </div>
  </div>
</template>
